import { types, NLP_STAGE } from 'actions/searchNLP';
import { types as searchTypes } from 'actions/search';
import { emptySessions, withSession } from './sessions';
import { feedbackEventHandlers } from './feedbackEvents';
import {
  buildTermSideMap,
  sharedAcrossSideLabels,
  sameSidePredicate,
} from './termSides';
import {
  toggleExplicitEntry,
  uniqStringsCi,
  seedTopNSelection,
  seedPluralSelectionForTopN,
  conflictingPluralLowers,
  dedupeTermSynonyms,
  dedupeTermPlurals,
  dedupeTermPluralSources,
  synonymSourcedPluralVariants,
  syncSynonymTogglesFromSelections,
  syncPluralTogglesFromSelections,
  toLowerSet,
  applySynonymChipToggleSelection,
  applyPluralChipToggleSelection,
} from './synonymPluralSelection';

// Number of related terms auto-selected per parent term on initial load (top-N by LlmRank,
// which the API returns in rank order). 0 = related terms are purely opt-in: everything
// renders deselected and the user picks what they want. The top-N seeding machinery
// (seedTopNSelection, initiallySelectedSynonyms, deselection logging) is kept intact so this
// stays a one-line change if a default-on window is ever wanted again.
export const DEFAULT_SELECTED_SYNONYM_LIMIT = 0;

// Foreign filter mutations replace the search wholesale; the AI session on screen ends with
// them here, in the reducer, so no entry point has to know a teardown checklist. Records
// survive (the cap evicts) so the feedback target keeps working where it should.
const filtersReplaced = state => ({
  ...state,
  activeSessionId: null,
  showAISearchFeedbackModal: false,
  pendingSearchView: null,
});

// User-initiated Clear/Reset also drops the feedback target.
const filtersCleared = state => ({
  ...filtersReplaced(state),
  confirmedSessionId: null,
});

const defaultState = {
  showNLPFilterModal: false,
  showAISearchFeedbackModal: false,
  recentSearches: [],
  nlpModal: {
    stage: NLP_STAGE.INPUT,
    query: '',
    aiSearchInterpretation: null,
    apiError: null,
    // Org profile only: true when the org endpoint says the query belongs on main search.
    orgMainSearchRedirect: false,
    // SCIL-18504: the one query kept after a failed close, as { text, surface }. Only the
    // close (RESET_NLP_MODAL_STATE) writes it and only the open (RESTORE_KEPT_NLP_QUERY) reads it.
    keptQuery: null,
    spellCheckPending: false,
    correctedQuery: '',
    spellCheckOriginalQuery: '',
    skipSpellCheck: false,
    skipClassification: false,
    ambiguityPending: false,
    pendingSuggestedTypes: null,
    savedFilters: null,
    selectedExampleId: null,
    synonymsEnabled: true,
    termEnabled: {},
    selectedSynonyms: {},
    // Snapshot of the top-N default-on synonyms per term (taken at seed time). Only a chip that
    // was in this set and is later turned off counts as a real deselection (SCIL-16099).
    initiallySelectedSynonyms: {},
    pluralsEnabled: true,
    pluralTermEnabled: {},
    selectedPluralVariants: {},
    // Chips the user turned ON that were OFF by default (beyond the top-N). Feeds the "remainder
    // that got selected" signal. Bulk master-toggle-on does not populate this.
    explicitlySelectedSynonyms: {},
    // parentTerm(lowercased) -> 'include' | 'exclude', derived from the Lucene term string.
    termSideMap: {},
    // Lowercased labels seeded OFF because they're shared across sides; excluded from
    // deselection logging so a default-off chip toggled on→off isn't counted.
    defaultOffLabels: [],
  },
  pendingSearchView: null,
  // Everything session-scoped lives in sessions.byId; these pointers decide which record the
  // UI sees. pendingSessionId: the session under construction/review while the modal is open.
  // activeSessionId: the session the on-screen results belong to. confirmedSessionId: the
  // feedback target — survives non-reset session ends so feedback persists across re-runs.
  sessions: emptySessions,
  pendingSessionId: null,
  activeSessionId: null,
  confirmedSessionId: null,
};

const REDUCER = {
  ...feedbackEventHandlers,
  // Cross-slice teardown: applying a saved search / alert / external search, restoring
  // persisted state, or clearing filters ends the AI session as part of the same dispatch.
  [searchTypes.SET_FILTERS_FROM_ALERT]: filtersReplaced,
  [searchTypes.SET_EXTERNAL_SEARCH_FROM_SAVED_SEARCH]: filtersReplaced,
  [searchTypes.PERFORM_EXTERNAL_SEARCH]: filtersReplaced,
  [searchTypes.RESTORE_STATE]: filtersReplaced,
  [searchTypes.RESET_FILTERS]: filtersCleared,
  [searchTypes.CLEAR_FILTERS]: filtersCleared,
  [types.SET_PENDING_SEARCH_VIEW]: (state, action) => ({
    ...state,
    pendingSearchView: action.payload.view || null,
  }),
  [types.CLEAR_PENDING_SEARCH_VIEW]: state => ({
    ...state,
    pendingSearchView: null,
  }),
  [types.SHOW_NLP_FILTER_MODAL]: (state, action) => {
    if (state.showNLPFilterModal === action.payload.showNLPFilterModal) {
      return state;
    }
    return {
      ...state,
      // Minted in the action creator when opening, null when closing. The record itself
      // materialises on the first write (generation), so open/close cycles leave no husks.
      pendingSessionId: action.payload.sessionId ?? null,
      showNLPFilterModal: action.payload.showNLPFilterModal,
    };
  },
  [types.SET_NLP_MODAL_STATE]: (state, action) => {
    const merged = { ...state.nlpModal, ...action.payload };
    const interp = action.payload.aiSearchInterpretation;

    let nextInterpretation = interp ? { ...interp } : null;

    const sideMap = interp
      ? buildTermSideMap(interp?.filters?.term)
      : state.nlpModal.termSideMap;
    if (interp) {
      merged.termSideMap = sideMap;
    }

    const ts = interp?.termSynonyms
      ? dedupeTermSynonyms(interp.termSynonyms)
      : null;
    const tp = interp?.termPlurals
      ? dedupeTermPlurals(interp.termPlurals)
      : null;
    const tps = dedupeTermPluralSources(interp?.termPluralSources);

    // Related terms / plurals that fall on BOTH an inclusion and an exclusion
    // parent are seeded OFF, so a shared term never silently lands on both sides
    // of the query (SCIL-16099). The user re-enables it per side if they want it.
    const sharedLowers =
      ts || tp
        ? sharedAcrossSideLabels(sideMap, ts || {}, tp || {})
        : new Set();

    // Labels that seeded OFF (shared across sides). A chip that was off by default
    // is not part of the auto-selected list, so toggling it on then off must NOT be
    // logged as a deselection (SCIL-16099 — per James, only manual deselections of
    // default-on chips feed the ranking signal).
    if (interp) {
      merged.defaultOffLabels = [...sharedLowers];
    }

    if (ts) {
      nextInterpretation = { ...nextInterpretation, termSynonyms: ts };
      // Default-select only the top-N related terms per parent term (same-side union), so the
      // long tail renders but starts off. The rest stay selectable; toggling them is not logged
      // as a deselection (see initiallySelectedSynonyms + buildDeselectionPayload).
      merged.selectedSynonyms = seedTopNSelection(
        ts,
        sharedLowers,
        sideMap,
        DEFAULT_SELECTED_SYNONYM_LIMIT
      );
      // Snapshot the default-on set per term. A deselection is the net difference between this
      // snapshot and the final selection, so beyond-top-N and shared-off chips (never in the
      // snapshot) can never count as deselections.
      merged.initiallySelectedSynonyms = Object.fromEntries(
        Object.entries(merged.selectedSynonyms).map(([t, sel]) => [t, [...sel]])
      );
      // Row + master toggles stay derived from chip state: on iff the row keeps a chip.
      merged.termEnabled = Object.fromEntries(
        Object.entries(merged.selectedSynonyms).map(([t, sel]) => [
          t,
          sel.length > 0,
        ])
      );
      merged.synonymsEnabled = Object.values(merged.selectedSynonyms).some(
        sel => sel.length > 0
      );
      merged.explicitlySelectedSynonyms = {};
    }

    if (tp) {
      nextInterpretation = {
        ...nextInterpretation,
        termPlurals: tp,
        ...(tps ? { termPluralSources: tps } : {}),
      };
      // Plurals of the AI-extracted base terms seed ON (cheap recall, same concept);
      // plurals sourced only from a related term seed OFF like the related terms
      // themselves (with the limit at 0 every synonym is default-off), and cascade in
      // when their related term is selected.
      //
      // Cross-side filtering uses the NARROWED conflict set (SCIL-17815), not the
      // blanket shared-across-sides set: with symmetric expansions ("cancer excluding
      // oncology") the blanket set contains every label and would strip the base-term
      // plurals — leaving "oncologies" NOT excluded despite "NOT oncology".
      const pluralConflictLowers = conflictingPluralLowers(
        tp,
        tps,
        ts,
        ts ? merged.selectedSynonyms : null,
        sideMap
      );
      merged.defaultOffLabels = [
        ...new Set([
          ...(merged.defaultOffLabels || []),
          ...pluralConflictLowers,
        ]),
      ];
      merged.selectedPluralVariants = seedPluralSelectionForTopN(
        tp,
        tps,
        ts,
        ts ? merged.selectedSynonyms : null,
        pluralConflictLowers
      );
      merged.pluralTermEnabled = Object.fromEntries(
        Object.entries(merged.selectedPluralVariants).map(([t, sel]) => [
          t,
          sel.length > 0,
        ])
      );
      merged.pluralsEnabled = Object.values(merged.selectedPluralVariants).some(
        sel => sel.length > 0
      );
    } else if (tps) {
      nextInterpretation = {
        ...nextInterpretation,
        termPluralSources: tps,
      };
    }

    if (nextInterpretation) {
      merged.aiSearchInterpretation = nextInterpretation;
    }

    return { ...state, nlpModal: merged };
  },
  [types.RESET_NLP_MODAL_STATE]: (state, action) => {
    const { keepQuery, surface } = action.payload ?? {};
    const text = state.nlpModal.query;
    return {
      ...state,
      nlpModal: {
        ...defaultState.nlpModal,
        keptQuery: keepQuery && text ? { text, surface } : null,
      },
    };
  },
  // The input wins: a prefill (?sciAiQuery=, the org empty state's Edit query) already set
  // `query`, so the kept one is dropped rather than overwriting it.
  [types.RESTORE_KEPT_NLP_QUERY]: (state, action) => {
    const kept = state.nlpModal.keptQuery;
    if (!kept) {
      return state;
    }
    const restore =
      kept.surface === action.payload.surface && !state.nlpModal.query;
    return {
      ...state,
      nlpModal: {
        ...state.nlpModal,
        keptQuery: null,
        ...(restore && { query: kept.text }),
      },
    };
  },
  // A generation arrived at the stage-two review: (re)start this session's record around it.
  // A fresh generation under an existing id supersedes any earlier confirmation — the user has
  // not searched with these filters yet.
  [types.AI_GENERATION_RECEIVED]: (state, action) => {
    const { sessionId, query, interpretation, generatedAt } = action.payload;
    if (!sessionId || !interpretation) {
      return state;
    }
    return {
      ...state,
      sessions: withSession(state, sessionId, () => ({
        provenance: null,
        naturalLanguageQuery: {
          queries: [interpretation.interpretedQuery || query],
        },
        filterResponse: interpretation,
        generatedAt,
        confirmedAt: null,
      })),
    };
  },
  // The user accepted the generated filters: promote the pending session to active + confirmed,
  // stamp provenance, and seed the conversation. One action so the transition is atomic.
  // An org-profile accept writes the record only: the active/confirmed pointers belong to main
  // search, and they drive its SciAI panel, persistence and restore (SCIL-18950).
  [types.SESSION_ACTIVATED]: (state, action) => {
    const id = state.pendingSessionId;
    if (!id) {
      return state;
    }
    const {
      queryText,
      naturalLanguageQuery,
      filterResponse,
      aiAppliedFingerprint,
      confirmedAt,
      organisationId,
    } = action.payload;
    const sessions = withSession(state, id, existing => ({
      provenance: {
        sessionId: id,
        naturalLanguageQuery,
        filterResponse,
        aiAppliedFingerprint,
      },
      // The record's query chain accumulates applied follow-ups, while the accept payload only
      // carries the original query — an existing chain always wins.
      naturalLanguageQuery: existing.naturalLanguageQuery?.queries?.length
        ? existing.naturalLanguageQuery
        : naturalLanguageQuery,
      filterResponse,
      confirmedAt,
      conversationMessages: [
        { key: '0', messageText: queryText, isUser: true },
        { key: '1', messageText: 'Let me fetch your results.', isUser: false },
      ],
      feedback: {
        ...existing.feedback,
        events: [
          ...existing.feedback.events,
          {
            action: 'userAcceptedGeneratedFilters',
            appliedFilters: filterResponse.filters,
            appliedSynonyms: filterResponse.effectiveSynonyms,
            appliedPlurals: filterResponse.effectivePlurals,
          },
        ],
      },
    }));
    return {
      ...state,
      sessions,
      ...(organisationId
        ? {}
        : { activeSessionId: id, confirmedSessionId: id }),
      pendingSessionId: null,
      pendingSearchView: null,
    };
  },
  // An AI-flagged saved search was loaded: a fresh session becomes active + confirmed in one
  // step, so follow-ups, feedback events, and insights calls all agree on the same id.
  [types.SESSION_RESUMED]: (state, action) => {
    const { sessionId, aiAppliedFingerprint, confirmedAt } = action.payload;
    if (!sessionId) {
      return state;
    }
    const filterResponse = { loadedFromSavedSearch: true };
    const sessions = withSession(state, sessionId, () => ({
      provenance: {
        sessionId,
        naturalLanguageQuery: { queries: [] },
        filterResponse,
        aiAppliedFingerprint,
      },
      filterResponse,
      confirmedAt,
    }));
    return {
      ...state,
      sessions,
      activeSessionId: sessionId,
      confirmedSessionId: sessionId,
    };
  },
  // A follow-up re-ran the search with merged filters: refresh the active session's baseline
  // fingerprint so the pre-search middleware doesn't end the session as diverged.
  [types.SESSION_REBASELINED]: (state, action) => {
    const id = state.activeSessionId;
    if (!id || !state.sessions.byId[id]?.provenance) {
      return state;
    }
    return {
      ...state,
      sessions: withSession(state, id, existing => ({
        provenance: {
          ...existing.provenance,
          aiAppliedFingerprint: action.payload.aiAppliedFingerprint,
        },
      })),
    };
  },
  // The session on screen is over. The record survives (under the cap) and the feedback target
  // with it; user-initiated Clear/Reset drops the target via the cross-slice handlers above.
  [types.SESSION_ENDED]: state => ({
    ...state,
    activeSessionId: null,
    showAISearchFeedbackModal: false,
  }),
  // Rehydrate the persisted subset on page load (dispatched by RestoreState, after the search
  // slice restores). Only session data survives a refresh — modal and other UI flags start at
  // defaults — and a pointer is only honoured when its record actually made it into storage.
  [types.RESTORE_STATE]: (state, action) => {
    const saved = action.payload;
    if (!saved?.sessions?.byId || !Array.isArray(saved.sessions.order)) {
      return state;
    }
    const { byId } = saved.sessions;
    return {
      ...state,
      sessions: saved.sessions,
      activeSessionId: byId[saved.activeSessionId]
        ? saved.activeSessionId
        : null,
      confirmedSessionId: byId[saved.confirmedSessionId]
        ? saved.confirmedSessionId
        : null,
      recentSearches: Array.isArray(saved.recentSearches)
        ? saved.recentSearches
        : state.recentSearches,
    };
  },
  // A follow-up query was applied: append its text to the confirmed session's query chain. The
  // chain travels to the feedback API as NaturalLanguageQuery.queries, so feedback reflects the
  // whole refinement path, not just the opening query.
  [types.AI_FOLLOW_UP_APPLIED]: (state, action) => {
    const id = state.confirmedSessionId;
    const record = id ? state.sessions.byId[id] : null;
    const { followUp } = action.payload;
    if (!record || !followUp?.text) {
      return state;
    }
    const queries = record.naturalLanguageQuery?.queries ?? [];
    // An accidental double-click re-applies the same filters; one chain entry is enough.
    if (queries[queries.length - 1] === followUp.text) {
      return state;
    }
    return {
      ...state,
      sessions: withSession(state, id, existing => ({
        naturalLanguageQuery: { queries: [...queries, followUp.text] },
        appliedFollowUps: [...existing.appliedFollowUps, followUp],
        feedback: {
          ...existing.feedback,
          queries: [...existing.feedback.queries, followUp.text],
          filters: { ...existing.feedback.filters, ...followUp.filters },
          events: [
            ...existing.feedback.events,
            { action: 'userAppliedFollowUpQuery', followUp },
          ],
        },
      })),
    };
  },
  [types.SHOW_AI_SEARCH_FEEDBACK_MODAL]: (state, action) => ({
    ...state,
    showAISearchFeedbackModal: action.payload.showAISearchFeedbackModal,
  }),
  [types.SET_RECENT_SEARCHES]: (state, action) => ({
    ...state,
    recentSearches: Array.isArray(action.payload.recentSearches)
      ? action.payload.recentSearches
      : [],
  }),
  [types.CLEAR_RECENT_SEARCHES]: state => ({
    ...state,
    recentSearches: [],
  }),
  [types.TOGGLE_SYNONYMS_ENABLED]: state => {
    const nextSynonymsEnabled = !state.nlpModal.synonymsEnabled;
    const interp = state.nlpModal.aiSearchInterpretation;
    const ts = interp?.termSynonyms;
    const allSources = interp?.termPluralSources || {};
    // "Select all" must still respect the default-off rule, or the master toggle
    // would become a backdoor to the include+exclude conflict (SCIL-16099).
    const shared = sharedAcrossSideLabels(
      state.nlpModal.termSideMap,
      ts || {},
      interp?.termPlurals || {}
    );
    let newTermEnabled = { ...state.nlpModal.termEnabled };
    let newSelectedSynonyms = { ...state.nlpModal.selectedSynonyms };

    // Master toggle = select all / deselect all for synonym rows (so children stay usable after bulk off).
    if (ts && typeof ts === 'object') {
      if (!nextSynonymsEnabled) {
        newTermEnabled = {
          ...newTermEnabled,
          ...Object.fromEntries(Object.keys(ts).map(t => [t, false])),
        };
        newSelectedSynonyms = {
          ...newSelectedSynonyms,
          ...Object.fromEntries(Object.keys(ts).map(t => [t, []])),
        };
      } else {
        const reselected = Object.fromEntries(
          Object.entries(ts).map(([t, syns]) => [
            t,
            uniqStringsCi(
              (Array.isArray(syns) ? syns : []).filter(
                s => !shared.has(s.toLowerCase())
              )
            ),
          ])
        );
        newSelectedSynonyms = { ...newSelectedSynonyms, ...reselected };
        newTermEnabled = {
          ...newTermEnabled,
          ...Object.fromEntries(
            Object.keys(ts).map(t => [t, (reselected[t] || []).length > 0])
          ),
        };
      }
    }

    let newSelectedPluralVariants = {
      ...state.nlpModal.selectedPluralVariants,
    };

    const termKeys = new Set([
      ...Object.keys(newSelectedPluralVariants),
      ...Object.keys(interp?.termPlurals || {}),
    ]);

    if (!nextSynonymsEnabled) {
      for (const term of termKeys) {
        const synonymDerived = synonymSourcedPluralVariants(
          term,
          allSources[term]
        );
        if (synonymDerived.length === 0) continue;
        const drop = new Set(synonymDerived.map(v => v.toLowerCase()));
        const cur = newSelectedPluralVariants[term] || [];
        newSelectedPluralVariants[term] = cur.filter(
          v => !drop.has(v.toLowerCase())
        );
      }
    } else {
      for (const term of termKeys) {
        if (newTermEnabled[term] === false) continue;
        const synonymDerived = synonymSourcedPluralVariants(
          term,
          allSources[term]
        ).filter(v => !shared.has(v.toLowerCase()));
        if (synonymDerived.length === 0) continue;
        const cur = newSelectedPluralVariants[term] || [];
        newSelectedPluralVariants[term] = uniqStringsCi([
          ...cur,
          ...synonymDerived,
        ]);
      }
    }

    // Keep plural row/master flags mirroring the cascaded plural selections (same invariant
    // the per-chip path enforces) — with opt-in defaults the rows start disabled, so a
    // cascaded-in plural must enable its row or it would never reach the query.
    const plSync = syncPluralTogglesFromSelections(
      newSelectedPluralVariants,
      interp?.termPlurals,
      state.nlpModal.pluralTermEnabled
    );

    return {
      ...state,
      nlpModal: {
        ...state.nlpModal,
        ...plSync,
        synonymsEnabled: nextSynonymsEnabled,
        termEnabled: newTermEnabled,
        selectedSynonyms: newSelectedSynonyms,
        selectedPluralVariants: newSelectedPluralVariants,
      },
    };
  },
  [types.TOGGLE_TERM_ENABLED]: (state, action) => {
    const { term } = action.payload;
    const prev = state.nlpModal.termEnabled[term];
    const toggledOn = !prev;

    const interp = state.nlpModal.aiSearchInterpretation;
    const allowTerm = sameSidePredicate(state.nlpModal.termSideMap, term);
    const rowSynonyms = interp?.termSynonyms?.[term] || [];
    const currentlySelected = state.nlpModal.selectedSynonyms[term] || [];

    const targetSynonyms = toggledOn ? rowSynonyms : [];
    const targetSynonymLowers = toLowerSet(targetSynonyms);
    const currentSynonymLowers = toLowerSet(currentlySelected);

    const chipsToToggle = toggledOn
      ? rowSynonyms.filter(s => !currentSynonymLowers.has(s.toLowerCase()))
      : currentlySelected.filter(
          s => !targetSynonymLowers.has(s.toLowerCase())
        );

    let newSelectedSynonyms = { ...state.nlpModal.selectedSynonyms };
    let newSelectedPluralVariants = {
      ...state.nlpModal.selectedPluralVariants,
    };

    for (const synonymChip of chipsToToggle) {
      const next = applySynonymChipToggleSelection(
        interp,
        newSelectedSynonyms,
        newSelectedPluralVariants,
        [synonymChip],
        toggledOn,
        allowTerm
      );
      newSelectedSynonyms = next.selectedSynonyms;
      newSelectedPluralVariants = next.selectedPluralVariants;
    }

    const synSync = syncSynonymTogglesFromSelections(
      newSelectedSynonyms,
      interp?.termSynonyms,
      state.nlpModal.termEnabled
    );
    const plSync = syncPluralTogglesFromSelections(
      newSelectedPluralVariants,
      interp?.termPlurals,
      state.nlpModal.pluralTermEnabled
    );

    return {
      ...state,
      nlpModal: {
        ...state.nlpModal,
        ...synSync,
        ...plSync,
        selectedSynonyms: newSelectedSynonyms,
        selectedPluralVariants: newSelectedPluralVariants,
      },
    };
  },
  [types.TOGGLE_SYNONYM]: (state, action) => {
    const { term, synonym } = action.payload;
    const interp = state.nlpModal.aiSearchInterpretation;
    const allowTerm = sameSidePredicate(state.nlpModal.termSideMap, term);
    const synLow = synonym.toLowerCase();

    const current = state.nlpModal.selectedSynonyms[term] || [];
    const isSelected = current.some(s => s.toLowerCase() === synLow);
    const isNowSelected = !isSelected;

    const toggled = applySynonymChipToggleSelection(
      interp,
      state.nlpModal.selectedSynonyms,
      state.nlpModal.selectedPluralVariants,
      [synonym],
      isNowSelected,
      allowTerm
    );
    const newSelectedSynonyms = toggled.selectedSynonyms;
    const newSelectedPluralVariants = toggled.selectedPluralVariants;

    const synSync = syncSynonymTogglesFromSelections(
      newSelectedSynonyms,
      interp?.termSynonyms,
      state.nlpModal.termEnabled
    );
    const plSync = syncPluralTogglesFromSelections(
      newSelectedPluralVariants,
      interp?.termPlurals,
      state.nlpModal.pluralTermEnabled
    );

    // Track the user's explicit per-chip ON-toggles of default-off chips. Bulk master toggles do
    // not touch this map, so only deliberate per-chip selections feed the "remainder selected"
    // signal. (Deselections are derived net-state from the default snapshot, not tracked here.)
    const newExplicitSelected = toggleExplicitEntry(
      state.nlpModal.explicitlySelectedSynonyms,
      term,
      synonym,
      isNowSelected
    );

    return {
      ...state,
      nlpModal: {
        ...state.nlpModal,
        ...synSync,
        ...plSync,
        selectedSynonyms: newSelectedSynonyms,
        selectedPluralVariants: newSelectedPluralVariants,
        explicitlySelectedSynonyms: newExplicitSelected,
      },
    };
  },
  [types.TOGGLE_PLURALS_ENABLED]: state => {
    const nextPluralsEnabled = !state.nlpModal.pluralsEnabled;
    const interp = state.nlpModal.aiSearchInterpretation;
    const tp = interp?.termPlurals;
    // Keep "select all" from creating a genuine cross-side conflict (SCIL-16099,
    // narrowed per SCIL-17815 — same rule the seeding uses, so off->on lands back
    // on the seeded state).
    const conflicts = conflictingPluralLowers(
      tp || {},
      interp?.termPluralSources,
      interp?.termSynonyms,
      state.nlpModal.selectedSynonyms || {},
      state.nlpModal.termSideMap
    );

    let newPluralTermEnabled = { ...state.nlpModal.pluralTermEnabled };
    let newSelectedPluralVariants = {
      ...state.nlpModal.selectedPluralVariants,
    };

    if (tp && typeof tp === 'object') {
      if (!nextPluralsEnabled) {
        newPluralTermEnabled = {
          ...newPluralTermEnabled,
          ...Object.fromEntries(Object.keys(tp).map(t => [t, false])),
        };
        newSelectedPluralVariants = {
          ...newSelectedPluralVariants,
          ...Object.fromEntries(Object.keys(tp).map(t => [t, []])),
        };
      } else {
        // Master ON selects plurals of the AI-extracted base terms (plus plurals of any
        // currently-selected related terms), NOT plurals sourced only from unselected
        // related terms — those would inject a concept the user never opted into.
        const reselected = seedPluralSelectionForTopN(
          tp,
          interp?.termPluralSources,
          interp?.termSynonyms,
          state.nlpModal.selectedSynonyms || {},
          conflicts
        );
        newSelectedPluralVariants = {
          ...newSelectedPluralVariants,
          ...reselected,
        };
        newPluralTermEnabled = {
          ...newPluralTermEnabled,
          ...Object.fromEntries(
            Object.keys(tp).map(t => [t, (reselected[t] || []).length > 0])
          ),
        };
      }
    }

    return {
      ...state,
      nlpModal: {
        ...state.nlpModal,
        pluralsEnabled: nextPluralsEnabled,
        pluralTermEnabled: newPluralTermEnabled,
        selectedPluralVariants: newSelectedPluralVariants,
      },
    };
  },
  [types.TOGGLE_PLURAL_TERM_ENABLED]: (state, action) => {
    const { term } = action.payload;
    const prev = state.nlpModal.pluralTermEnabled[term];
    const toggledOn = !prev;

    const interp = state.nlpModal.aiSearchInterpretation;
    const allowTerm = sameSidePredicate(state.nlpModal.termSideMap, term);
    const rowVariants = interp?.termPlurals?.[term] || [];
    const currentlySelected = state.nlpModal.selectedPluralVariants[term] || [];
    // Row ON mirrors the master toggle's opt-in rule: select this row's base-term-sourced
    // plurals (plus plurals of its currently-selected related terms), not plurals sourced
    // only from unselected related terms. Cross-side filtering uses the narrowed conflict
    // set (SCIL-17815) computed over ALL terms, since conflicts are cross-row by nature.
    const rowConflicts = conflictingPluralLowers(
      interp?.termPlurals || {},
      interp?.termPluralSources,
      interp?.termSynonyms,
      state.nlpModal.selectedSynonyms || {},
      state.nlpModal.termSideMap
    );
    const targetVariants = toggledOn
      ? seedPluralSelectionForTopN(
          { [term]: rowVariants },
          { [term]: interp?.termPluralSources?.[term] },
          { [term]: interp?.termSynonyms?.[term] || [] },
          { [term]: state.nlpModal.selectedSynonyms?.[term] || [] },
          rowConflicts
        )[term] || []
      : [];
    const targetVariantLowers = toLowerSet(targetVariants);
    const currentVariantLowers = toLowerSet(currentlySelected);

    const chipsToToggle = toggledOn
      ? targetVariants.filter(v => !currentVariantLowers.has(v.toLowerCase()))
      : currentlySelected.filter(
          v => !targetVariantLowers.has(v.toLowerCase())
        );

    let newSelectedSynonyms = { ...state.nlpModal.selectedSynonyms };
    let newSelectedPluralVariants = {
      ...state.nlpModal.selectedPluralVariants,
    };

    for (const variantChip of chipsToToggle) {
      const next = applyPluralChipToggleSelection(
        interp,
        newSelectedSynonyms,
        newSelectedPluralVariants,
        [variantChip],
        toggledOn,
        allowTerm
      );
      newSelectedSynonyms = next.selectedSynonyms;
      newSelectedPluralVariants = next.selectedPluralVariants;
    }

    const plSync = syncPluralTogglesFromSelections(
      newSelectedPluralVariants,
      interp?.termPlurals,
      state.nlpModal.pluralTermEnabled
    );

    const synSync = syncSynonymTogglesFromSelections(
      newSelectedSynonyms,
      interp?.termSynonyms,
      state.nlpModal.termEnabled
    );

    return {
      ...state,
      nlpModal: {
        ...state.nlpModal,
        ...plSync,
        ...synSync,
        selectedSynonyms: newSelectedSynonyms,
        selectedPluralVariants: newSelectedPluralVariants,
      },
    };
  },
  [types.TOGGLE_PLURAL_VARIANT]: (state, action) => {
    const { term, variant } = action.payload;
    const interp = state.nlpModal.aiSearchInterpretation;
    const allowTerm = sameSidePredicate(state.nlpModal.termSideMap, term);
    const vLow = variant.toLowerCase();

    const current = state.nlpModal.selectedPluralVariants[term] || [];
    const isSelected = current.some(v => v.toLowerCase() === vLow);
    const isNowSelected = !isSelected;

    const toggled = applyPluralChipToggleSelection(
      interp,
      state.nlpModal.selectedSynonyms,
      state.nlpModal.selectedPluralVariants,
      [variant],
      isNowSelected,
      allowTerm
    );
    const newSelectedSynonyms = toggled.selectedSynonyms;
    const mergedPluralVariants = toggled.selectedPluralVariants;

    const plSync = syncPluralTogglesFromSelections(
      mergedPluralVariants,
      interp?.termPlurals,
      state.nlpModal.pluralTermEnabled
    );
    const synSync = syncSynonymTogglesFromSelections(
      newSelectedSynonyms,
      interp?.termSynonyms,
      state.nlpModal.termEnabled
    );

    return {
      ...state,
      nlpModal: {
        ...state.nlpModal,
        ...plSync,
        ...synSync,
        selectedSynonyms: newSelectedSynonyms,
        selectedPluralVariants: mergedPluralVariants,
      },
    };
  },
  // The transcript belongs to the active session; without one there is nothing to say it under.
  [types.CONVERSATION_MESSAGE_SENT]: (state, action) => {
    const { messageText, isUser } = action.payload;
    if (
      typeof messageText !== 'string' ||
      messageText.length <= 0 ||
      typeof isUser !== 'boolean'
    ) {
      return state;
    }
    const id = state.activeSessionId;
    if (!id || !state.sessions.byId[id]) {
      return state;
    }
    return {
      ...state,
      sessions: withSession(state, id, existing => ({
        conversationMessages: [
          ...existing.conversationMessages,
          {
            key: existing.conversationMessages.length.toString(),
            messageText,
            isUser,
          },
        ],
      })),
    };
  },
  // Follow-up suggestions for the search currently on screen, keyed by the as-executed filter
  // fingerprint so a refinement round replaces them; count caps rounds per session.
  [types.FOLLOW_UP_QUERIES_RECEIVED]: (state, action) => {
    const { sessionId, fingerprint, queries } = action.payload;
    if (!sessionId) {
      return state;
    }
    return {
      ...state,
      sessions: withSession(state, sessionId, existing => ({
        followUps: {
          sessionId,
          fingerprint,
          queries: Array.isArray(queries) ? queries : [],
          count: existing.followUps.count + 1,
        },
      })),
    };
  },
};

export const reducer = (state = defaultState, action) => {
  const handler = REDUCER[action.type];
  return handler ? handler(state, action) : state;
};
